/* ==========================================================================
   Spark PDP — buy-area behaviour
   --------------------------------------------------------------------------
   Companion to assets/spark.js, same conventions: vanilla JS, data-spark-pdp-*
   hooks, progressive enhancement only. The form posts natively: selling_plan
   and quantity are real inputs; id is a hidden input this file syncs from
   the option chips (no-JS posts the default variant). Labels, prices,
   variant images and the sticky bar all sync from the JSON map rendered by
   snippets/spark-pdp-buybox.liquid — nothing is hardcoded.
   ========================================================================== */

(function () {
  "use strict";

  var prefersReducedMotion = window.matchMedia(
    "(prefers-reduced-motion: reduce)",
  ).matches;

  /* ------------------------------------------------------------------
     Gallery: one swap path shared by thumbnail clicks and variant-image
     changes. Thumbs carry their full-size src in data-full-src; the main
     image mirrors its current src in data-image-src so both sides can be
     matched. Each swap re-triggers the fade animation.
     ------------------------------------------------------------------ */
  var mainImg = document.querySelector("[data-spark-pdp-main-img]");
  var thumbs = document.querySelectorAll("[data-spark-pdp-thumb]");

  /* ------------------------------------------------------------------
     Mobile carousel: a native-swipe, auto-advancing strip of every
     product image shown only below the buy breakpoint. Self-contained —
     the scroll position is the single source of truth; dots follow it,
     auto-advance nudges it and steps aside for the user. setMainImage
     calls syncCarousel so a variant change jumps the strip too.
     ------------------------------------------------------------------ */
  var syncCarousel = function () {}; // no-op until the carousel wires up
  var filterCarousel = function () {}; // no-op until the carousel wires up
  var promoteFirst = function () {}; // no-op until the carousel wires up

  // A slide / dot / thumb is visible when it carries no variant ids (a general,
  // shared image) or when its comma-separated id list includes the selected
  // variant. Empty/missing attribute -> always shown.
  var variantVisible = function (el, variantId) {
    var raw = el.getAttribute("data-variant-ids");
    // Trim before testing emptiness: the attribute is produced by a Liquid
    // capture, and a whitespace-only value is truthy — which would flip this
    // from "shared by every variant" to "shown for none of them".
    raw = raw ? raw.trim() : "";
    if (!raw) return true;
    var target = String(variantId);
    var ids = raw.split(",");
    for (var i = 0; i < ids.length; i += 1) {
      if (ids[i].trim() === target) return true;
    }
    return false;
  };

  (function () {
    var root = document.querySelector("[data-spark-pdp-carousel]");
    var track = root && root.querySelector("[data-spark-pdp-track]");
    if (!track) return;
    var slides = root.querySelectorAll("[data-spark-pdp-slide]");
    var dots = root.querySelectorAll("[data-spark-pdp-dot]");
    var counter = root.querySelector("[data-spark-pdp-counter]");
    if (!slides.length) return;

    // Filtered slides/dots are display:none and collapse to zero width, so all
    // index maths runs over the currently-visible subset only.
    var isDisplayed = function (el) {
      return el.style.display !== "none";
    };
    // Read LIVE DOM order, not the static NodeList: promoteFirst() below
    // reorders slides so the selected size leads, and every index calculation
    // here (scroll offset, active dot) has to follow that new order.
    var dotsWrap = dots.length ? dots[0].parentNode : null;
    var visibleSlides = function () {
      return Array.prototype.filter.call(track.children, isDisplayed);
    };
    var visibleDots = function () {
      if (!dotsWrap) return [];
      return Array.prototype.filter.call(dotsWrap.children, isDisplayed);
    };

    var currentIndex = function () {
      var w = track.clientWidth;
      return w ? Math.round(track.scrollLeft / w) : 0;
    };

    var setActiveDot = function (index) {
      var vis = visibleDots();
      dots.forEach(function (dot) {
        dot.removeAttribute("aria-current");
      });
      if (vis[index]) vis[index].setAttribute("aria-current", "true");
      if (counter) {
        var total = visibleSlides().length;
        counter.textContent = Math.min(index + 1, total) + " / " + total;
      }
    };

    var goTo = function (index, smooth) {
      var count = visibleSlides().length;
      if (index < 0) index = 0;
      if (index > count - 1) index = count - 1;
      track.scrollTo({
        left: index * track.clientWidth,
        behavior: smooth ? "smooth" : "auto",
      });
    };

    // Auto-advance, paused while the user is touching and while hidden.
    var timer = null;
    var resumeTimer = null;
    var canAuto = slides.length > 1 && !prefersReducedMotion;

    var tick = function () {
      if (document.hidden) return;
      var next = currentIndex() + 1;
      if (next > visibleSlides().length - 1) next = 0;
      goTo(next, true);
    };
    var startAuto = function () {
      if (!canAuto || timer || document.hidden) return;
      timer = window.setInterval(tick, 4500);
    };
    var stopAuto = function () {
      if (timer) {
        window.clearInterval(timer);
        timer = null;
      }
    };
    // Step aside on interaction, then resume after a quiet spell.
    var pauseAuto = function () {
      if (!canAuto) return;
      stopAuto();
      if (resumeTimer) window.clearTimeout(resumeTimer);
      resumeTimer = window.setTimeout(function () {
        resumeTimer = null;
        startAuto();
      }, 9000);
    };

    // Dots follow swipes; debounced to one read per animation frame.
    var scrollScheduled = false;
    track.addEventListener(
      "scroll",
      function () {
        if (scrollScheduled) return;
        scrollScheduled = true;
        window.requestAnimationFrame(function () {
          scrollScheduled = false;
          setActiveDot(currentIndex());
        });
      },
      { passive: true },
    );

    dots.forEach(function (dot) {
      dot.addEventListener("click", function () {
        var index = visibleDots().indexOf(dot);
        if (index < 0) return;
        pauseAuto();
        goTo(index, true);
      });
    });

    ["pointerdown", "touchstart", "wheel"].forEach(function (evt) {
      track.addEventListener(evt, pauseAuto, { passive: true });
    });

    document.addEventListener("visibilitychange", function () {
      if (document.hidden) {
        stopAuto();
      } else {
        startAuto();
      }
    });

    // Jump the strip to the variant's image without smoothing, then hold
    // auto-advance so the user sees the variant they just picked.
    syncCarousel = function (src) {
      if (!src) return;
      var vis = visibleSlides();
      for (var i = 0; i < vis.length; i += 1) {
        if (vis[i].getAttribute("data-slide-src") === src) {
          goTo(i, false);
          setActiveDot(i);
          pauseAuto();
          return;
        }
      }
    };

    // Hide any slide/dot that belongs to a different variant, keeping dots 1:1
    // with slides (same rule, same order), then reset to the first visible one.
    filterCarousel = function (variantId) {
      for (var i = 0; i < slides.length; i += 1) {
        var vis = variantVisible(slides[i], variantId);
        slides[i].style.display = vis ? "" : "none";
        if (dots[i]) dots[i].style.display = vis ? "" : "none";
      }
      track.scrollTo({ left: 0 });
      setActiveDot(0);
    };

    // Make the selected variant's image the FIRST image instead of scrolling to
    // wherever it happens to sit. Picking a size used to fling the strip to
    // slide 9 of 13, which reads as "the page jumped" rather than "here is your
    // size". Moving the slide (and its dot, in lockstep so the two stay paired
    // by NodeList index) to the front leaves the shopper at position 1 looking
    // at their own size.
    promoteFirst = function (src) {
      if (!src) return;
      for (var i = 0; i < slides.length; i += 1) {
        if (slides[i].getAttribute("data-slide-src") !== src) continue;
        if (slides[i] !== track.firstElementChild) {
          track.insertBefore(slides[i], track.firstElementChild);
          if (dots[i] && dotsWrap) {
            dotsWrap.insertBefore(dots[i], dotsWrap.firstElementChild);
          }
        }
        break;
      }
      track.scrollTo({ left: 0 });
      setActiveDot(0);
      pauseAuto();
    };

    startAuto();
  })();

  // Show only the shared images plus those attached to the selected variant,
  // across the mobile carousel (slides + dots) and the desktop thumbnail rail.
  var filterGallery = function (variantId) {
    filterCarousel(variantId);
    thumbs.forEach(function (thumb) {
      thumb.style.display = variantVisible(thumb, variantId) ? "" : "none";
    });
  };

  // Variant change: lead with that variant's image everywhere — its carousel
  // slide first, its thumbnail first in the desktop rail. Deliberately NOT
  // called from thumbnail clicks: reordering the rail under the shopper's finger
  // while they browse would be disorienting. Only picking a size reorders.
  var leadWithImage = function (src) {
    if (!src) return;
    promoteFirst(src);
    for (var i = 0; i < thumbs.length; i += 1) {
      if (thumbs[i].getAttribute("data-full-src") !== src) continue;
      var rail = thumbs[i].parentNode;
      if (rail && thumbs[i] !== rail.firstElementChild) {
        rail.insertBefore(thumbs[i], rail.firstElementChild);
      }
      break;
    }
  };

  var setMainImage = function (src, srcset, alt) {
    if (!mainImg || !src) return;
    if (src === mainImg.getAttribute("data-image-src")) return;
    mainImg.setAttribute("src", src);
    mainImg.setAttribute("srcset", srcset || "");
    mainImg.setAttribute("alt", alt || "");
    mainImg.setAttribute("data-image-src", src);
    syncCarousel(src);
    thumbs.forEach(function (thumb) {
      thumb.classList.toggle(
        "is-active",
        thumb.getAttribute("data-full-src") === src,
      );
    });
    if (!prefersReducedMotion) {
      mainImg.classList.remove("spark-pdp-gallery__fade");
      void mainImg.offsetWidth; // restart the CSS animation
      mainImg.classList.add("spark-pdp-gallery__fade");
    }
  };

  thumbs.forEach(function (thumb) {
    thumb.addEventListener("click", function () {
      setMainImage(
        thumb.getAttribute("data-full-src"),
        thumb.getAttribute("data-full-srcset"),
        thumb.getAttribute("data-full-alt"),
      );
    });
  });

  /* ------------------------------------------------------------------
     THE ADD-TO-CART GUARD: nothing reaches the cart without a variant.
     ------------------------------------------------------------------
     The hidden id input ships EMPTY while the size is unchosen (a size nobody
     picked is a return, not a sale), and an empty id at /cart/add is answered
     with Shopify's bare "Cannot find variant". This block makes sure no route
     gets there.

     WHY IT IS ITS OWN BLOCK, AND WHY IT LOOKS EVERYTHING UP LIVE. The first
     version lived inside the buy-form block and tested node references captured
     at load. It held in every test we could run — and real shoppers still hit
     the error page. The tests could not see why: Coframe and Intelligems classify
     a headless browser as a bot (x-is-bot) and serve it no experiments, while a
     real visitor gets experiments that inject into this very buy box, analytics
     that bind to the add-to-cart button, and apps that may add to the cart
     themselves. So nothing here trusts a captured node or a listener order. Every
     check reads the form as it is in the DOM at that moment, and every route to
     the cart is closed at the earliest point the page offers:

       1. CLICK on any submit button of the form, at the window in the capture
          phase. That is ahead of every listener on the document, the form and the
          button itself, whoever registered first — so a script that would add to
          the cart from its own click handler never sees the click.
       2. SUBMIT of the form, same place, for Enter in a field and requestSubmit().
       3. form.submit(), which fires no submit event and skips validation.
       4. A fetch or XHR to /cart/add carrying a blank id — certain to fail, so it
          is refused here and answered in Shopify's own 422 shape with a sentence
          a cart UI can show, instead of "Cannot find variant".

     And the no-JS net stays where it was: `required` on the size <select>. */
  var GUARD_FORM_ID = "spark-pdp-form";
  // The buy-form block hands its update() over, so a guard check can re-read
  // the controls before deciding (see guardReason).
  var guardResync = null;

  var guardParts = function () {
    var f = document.getElementById(GUARD_FORM_ID);
    if (!f) return null;
    var size = f.querySelector("[data-spark-size-select]");
    return {
      form: f,
      id: f.querySelector('input[name="id"]'),
      size: size,
      wrap: size ? size.closest(".spark-pdp-select") : null,
      prompt: f.querySelector("[data-spark-size-prompt]"),
    };
  };

  // "size" while no size is chosen; "variant" while the chosen options name no
  // variant at all; null when the form can post.
  var guardReason = function (g) {
    if (!g) return null;
    if (g.size && !g.size.value) return "size";
    if (g.id && !g.id.value) {
      // A RESTORED PAGE can show a size the id input never heard about: the
      // browser puts the shopper's last choice back into the <select> after this
      // file has already synced the id from the empty one. Re-sync before
      // refusing, so a size that is visibly chosen is honoured.
      if (guardResync) {
        try {
          guardResync();
        } catch (e) {
          /* A failed re-sync leaves the id empty, which refuses below. */
        }
      }
      if (g.size && !g.size.value) return "size";
      if (!g.id.value) return "variant";
    }
    return null;
  };

  // THE BOUNCE WAITS FOR THE SCROLL. The control is usually off screen when the
  // shopper presses the button — it is above them — and a bounce played during
  // the smooth scroll that brings it into view is over before it arrives. So it
  // plays when the scroll ends (scrollend, with a timer for browsers without it),
  // or at once when the control is already in view.
  var playWhenSettled = function (el, cls) {
    var play = function () {
      el.classList.remove(cls);
      void el.offsetWidth; // restart the animation
      el.classList.add(cls);
    };
    var r = el.getBoundingClientRect();
    var vh = window.innerHeight || document.documentElement.clientHeight;
    if ((r.top >= 0 && r.bottom <= vh) || prefersReducedMotion) {
      play();
      return;
    }
    var played = false;
    var go = function () {
      if (played) return;
      played = true;
      window.removeEventListener("scrollend", go);
      play();
    };
    if ("onscrollend" in window) window.addEventListener("scrollend", go);
    window.setTimeout(go, 700);
  };

  // Back to the size control: scroll it to the middle of the screen, highlight
  // it (red edge, soft red fill — .is-invalid, which stays until a size is
  // picked), bounce it once it has arrived, say what is needed under it, and
  // put focus on it without opening anything.
  var showBlocked = function (g, reason) {
    if (!g) return;
    var wrap =
      g.wrap || g.form.querySelector(".spark-pdp-field .spark-pdp-select");
    var field = (wrap && wrap.closest(".spark-pdp-field")) || wrap;
    if (field) {
      field.scrollIntoView({
        behavior: prefersReducedMotion ? "auto" : "smooth",
        block: "center",
      });
    }
    if (wrap) {
      wrap.classList.add("is-invalid");
      playWhenSettled(wrap, "is-bounce");
      var ctl = wrap.querySelector("select");
      if (ctl) ctl.setAttribute("aria-invalid", "true");
    }
    if (g.prompt) {
      g.prompt.textContent =
        reason === "variant" ? "Choose an available size" : "Choose your size";
    }
    var face =
      (wrap && wrap.querySelector(".spark-sel__btn")) ||
      (wrap && wrap.querySelector("select"));
    if (face) {
      try {
        face.focus({ preventScroll: true });
      } catch (e) {
        /* Older browsers ignore the options bag; the scroll is already queued. */
      }
    }
  };

  // A size was picked: the highlight, the line and the invalid state all go.
  var clearBlocked = function () {
    var g = guardParts();
    if (!g || (g.size && !g.size.value)) return;
    if (g.wrap) {
      g.wrap.classList.remove("is-invalid", "is-bounce");
      g.wrap.removeAttribute("data-spark-size-unpicked");
    }
    if (g.size) g.size.removeAttribute("aria-invalid");
    if (g.prompt) g.prompt.textContent = "";
  };

  (function () {
    if (!guardParts()) return;

    /* NATIVE VALIDATION STANDS DOWN WHERE THIS FILE RUNS. `required` on the size
       select stops a visitor whose JS never ran. Constraint validation fires
       before the submit event, so left on it would replace the scroll, the
       highlight and the line with the browser's own grey bubble. */
    guardParts().form.noValidate = true;

    var refuse = function (event, g, reason) {
      event.preventDefault();
      if (event.stopImmediatePropagation) event.stopImmediatePropagation();
      event.stopPropagation();
      showBlocked(g, reason);
    };

    // 1. Clicks on a submit button of the buy form.
    window.addEventListener(
      "click",
      function (event) {
        var t = event.target;
        if (!t || typeof t.closest !== "function") return;
        var btn = t.closest('button, input[type="submit"], input[type="image"]');
        if (!btn) return;
        var g = guardParts();
        if (!g || btn.form !== g.form) return;
        var type = (btn.getAttribute("type") || "submit").toLowerCase();
        if (type !== "submit" && type !== "image") return;
        var reason = guardReason(g);
        if (reason) refuse(event, g, reason);
      },
      true,
    );

    // 2. Submits of the buy form (Enter in a field, requestSubmit()).
    window.addEventListener(
      "submit",
      function (event) {
        var g = guardParts();
        if (!g || event.target !== g.form) return;
        var reason = guardReason(g);
        if (reason) refuse(event, g, reason);
      },
      true,
    );

    // 3. form.submit() — no event, no validation. Scoped to this one form.
    // getAttribute, NOT this.id: the form holds an <input name="id">, and a form's
    // named controls shadow its properties, so `form.id` is that input element and
    // never equals the string — the check silently matched nothing.
    var nativeSubmit = HTMLFormElement.prototype.submit;
    HTMLFormElement.prototype.submit = function () {
      if (this && this.getAttribute && this.getAttribute("id") === GUARD_FORM_ID) {
        var g = guardParts();
        var reason = guardReason(g);
        if (reason) {
          showBlocked(g, reason);
          return;
        }
      }
      return nativeSubmit.apply(this, arguments);
    };

    // 4. Background adds with a blank id. Refused ONLY when certain to fail:
    // the request carries an `id` key whose value is blank and no items[] —
    // exactly what this form serialises to while the size is unchosen. Anything
    // else, including adds this file cannot parse, passes untouched.
    var ADD_URL = /\/cart\/add(?:\.js|\.json)?(?:[?#]|$)/;
    var blankIdAdd = function (url, body) {
      if (!ADD_URL.test(String(url || ""))) return false;
      try {
        var fields = null;
        if (body && typeof body.get === "function" && typeof body.has === "function") {
          fields = body; // FormData or URLSearchParams
        } else if (typeof body === "string") {
          var s = body.trim();
          if (s.charAt(0) === "{") {
            var j = JSON.parse(s);
            if (Array.isArray(j.items) && j.items.length) return false;
            return "id" in j && (j.id === null || String(j.id).trim() === "");
          }
          fields = new URLSearchParams(s);
        } else if (body && typeof body === "object") {
          if (Array.isArray(body.items) && body.items.length) return false;
          return "id" in body && (body.id === null || String(body.id).trim() === "");
        } else {
          var q = String(url).split("?")[1];
          if (!q) return false;
          fields = new URLSearchParams(q.split("#")[0]);
        }
        var hasItems = false;
        if (typeof fields.forEach === "function") {
          fields.forEach(function (v, k) {
            if (/^items/.test(k)) hasItems = true;
          });
        }
        return !hasItems && fields.has("id") && String(fields.get("id") || "").trim() === "";
      } catch (e) {
        return false;
      }
    };
    var onBlankAdd = function () {
      var g = guardParts();
      showBlocked(g, guardReason(g) || "size");
    };

    if (typeof window.fetch === "function") {
      var nativeFetch = window.fetch;
      window.fetch = function (input, init) {
        var url = typeof input === "string" ? input : input && input.url;
        var body = init && init.body;
        if (blankIdAdd(url, body)) {
          onBlankAdd();
          return Promise.resolve(
            new Response(
              JSON.stringify({ status: 422, message: "Cart Error", description: "Choose your size" }),
              { status: 422, headers: { "Content-Type": "application/json" } },
            ),
          );
        }
        return nativeFetch.apply(this, arguments);
      };
    }

    if (window.XMLHttpRequest) {
      var xopen = XMLHttpRequest.prototype.open;
      var xsend = XMLHttpRequest.prototype.send;
      XMLHttpRequest.prototype.open = function (method, url) {
        this.__sparkAddUrl = url;
        return xopen.apply(this, arguments);
      };
      XMLHttpRequest.prototype.send = function (body) {
        if (blankIdAdd(this.__sparkAddUrl, body)) {
          onBlankAdd();
          return; // never sent; nothing reaches the cart
        }
        return xsend.apply(this, arguments);
      };
    }
  })();

  /* ------------------------------------------------------------------
     Buy form: keep CTA labels, card prices and the sticky bar in sync
     with the selected variant, plan, frequency and quantity.
     ------------------------------------------------------------------ */
  var form = document.getElementById("spark-pdp-form");
  if (form) {
    var data = null;
    var dataEl = document.querySelector("[data-spark-pdp-data]");
    if (dataEl) {
      try {
        data = JSON.parse(dataEl.textContent);
      } catch (err) {
        // Malformed price map: the server-rendered labels stay correct for
        // the default selection, so leave everything static.
        data = null;
      }
    }

    var idInput = form.querySelector("[data-spark-pdp-id]");
    var optionInputs = form.querySelectorAll("[data-spark-pdp-option]");
    var planInputs = form.querySelectorAll("[data-spark-pdp-plan]");
    // QUANTITY is four radios called "quantity": 1, 2, 3, and a fourth whose value is
    // whatever its own stepper shows (4 by default, 4 to 20). Being one radio group is
    // the whole contract — exactly one is checked, so exactly one posts.
    var qtyPresets = form.querySelectorAll("[data-spark-pdp-qty-preset]");
    var qtyMoreRadio = form.querySelector("[data-spark-pdp-qty-more-radio]");
    var qtyMoreFace = form.querySelector("[data-spark-pdp-qty-more-face]");
    var qtyMoreMinus = form.querySelector("[data-spark-pdp-qty-minus]");
    var qtyMorePlus = form.querySelector("[data-spark-pdp-qty-plus]");
    var QTY_MORE_MIN = 4; // 1 to 3 are the tiles to its left
    var QTY_MORE_MAX = 20; // largest quantity in the store's sampled web orders
    var oneCard = form.querySelector('[data-spark-pdp-card="one"]');
    // Both purchase-option cards: the subscription group and one-time. Each owns
    // its own plan radios, so "selected" simply means "contains the checked
    // radio".
    var planCards = form.querySelectorAll("[data-spark-pdp-card]");
    var lastPlanId = null;

    // SIZE STARTS UNCHOSEN (see size_unpicked in spark-pdp-buybox.liquid). The
    // size <select> opens on an empty, disabled placeholder; until the shopper
    // picks a real size the id input stays empty, the CTA reads "Choose your
    // size", and a tap on it (or on the sticky bar) brings them to this control
    // with a calm one-line nudge instead of adding anything to the cart.
    var sizeSelect = form.querySelector("[data-spark-size-select]");
    var sizeWrap = sizeSelect ? sizeSelect.closest(".spark-pdp-select") : null;
    var sizeField = sizeSelect ? sizeSelect.closest(".spark-pdp-field") : null;
    var sizePrompt = form.querySelector("[data-spark-size-prompt]");
    var wallets = form.querySelector("[data-spark-pdp-wallets]");
    var sizeUnpicked = function () {
      return !!sizeSelect && !sizeSelect.value;
    };

    var checkedInput = function (inputs) {
      for (var i = 0; i < inputs.length; i += 1) {
        if (inputs[i].checked) return inputs[i];
      }
      return null;
    };

    var money = function (cents) {
      var symbol = (data && data.currency) || "$";
      return symbol + (cents / 100).toFixed(2);
    };

    var setText = function (selector, text) {
      document.querySelectorAll(selector).forEach(function (el) {
        el.textContent = text;
      });
    };

    // The quantity that will post: the checked radio's value. The fourth tile's radio
    // carries its stepper's number (see setMore), so this needs no special case.
    var clampQty = function () {
      var preset = checkedInput(qtyPresets);
      var qty = preset ? parseInt(preset.value, 10) : 1;
      return qty > 0 ? qty : 1;
    };

    // The stepper tile's number: written onto its radio (what posts), its accessible
    // name and the face, and the buttons stand down at the ends of the range — all from
    // one value, so the three can never disagree. `choose` also selects the tile.
    var setMore = function (n, choose) {
      if (!qtyMoreRadio) return;
      n = Math.min(QTY_MORE_MAX, Math.max(QTY_MORE_MIN, n || QTY_MORE_MIN));
      qtyMoreRadio.value = String(n);
      qtyMoreRadio.setAttribute("aria-label", String(n));
      if (qtyMoreFace) qtyMoreFace.textContent = String(n);
      if (qtyMoreMinus) qtyMoreMinus.disabled = n <= QTY_MORE_MIN;
      if (qtyMorePlus) qtyMorePlus.disabled = n >= QTY_MORE_MAX;
      if (choose) qtyMoreRadio.checked = true;
    };

    var findPlan = function (variant, planId) {
      var plans = (variant && variant.plans) || [];
      for (var i = 0; i < plans.length; i += 1) {
        if (String(plans[i].id) === String(planId)) return plans[i];
      }
      return null;
    };

    // Price shown on a card = its checked frequency, else its first available
    // one. Each card prices itself; they must never share a value.
    var cardPlanId = function (card, variant) {
      var inputs = card.querySelectorAll("[data-spark-pdp-plan]");
      var first = null;
      for (var i = 0; i < inputs.length; i += 1) {
        if (!inputs[i].value) continue;
        if (!findPlan(variant, inputs[i].value)) continue;
        if (inputs[i].checked) return inputs[i].value;
        if (!first) first = inputs[i].value;
      }
      return first;
    };

    // Resolve the variant matching the selected value of every option control
    // (option index -> value). Controls are radios (colour swatches) or native
    // <select>s (size). Returns null when no variant carries that combination.
    //
    // An EMPTY select value (the unchosen size placeholder) counts as "no
    // control at this index", so it matches every size. matchingVariants()
    // returns all of them — what the "From" prices are computed over — and the
    // first one stands in for the gallery and plan bookkeeping only; it is never
    // posted.
    var selectedOptions = function () {
      var selected = [];
      optionInputs.forEach(function (input) {
        var isSelect = input.tagName === "SELECT";
        if (!isSelect && !input.checked) return;
        if (isSelect && !input.value) return;
        var index = parseInt(input.getAttribute("data-option-index"), 10);
        if (!isNaN(index)) selected[index] = input.value;
      });
      return selected;
    };

    var matchingVariants = function () {
      var selected = selectedOptions();
      var out = [];
      var ids = Object.keys(data.variants);
      for (var i = 0; i < ids.length; i += 1) {
        var candidate = data.variants[ids[i]];
        var matches = true;
        for (var j = 0; j < selected.length; j += 1) {
          if (selected[j] === undefined) continue;
          if (!candidate.options || candidate.options[j] !== selected[j]) {
            matches = false;
            break;
          }
        }
        if (matches) out.push({ id: ids[i], variant: candidate });
      }
      return out;
    };

    var setCtasDisabled = function (disabled) {
      document
        .querySelectorAll("[data-spark-pdp-cta], [data-spark-pdp-sticky-cta]")
        .forEach(function (button) {
          button.disabled = disabled;
        });
    };

    // The cadence UI is a native <select>; the form-posting inputs remain the
    // hidden radios in [data-spark-freq-radios]. This mirrors radio state ->
    // select (update() calls it last, so recovery fallbacks are reflected too).
    // The reverse direction is the change listener further down.
    //
    // Each cadence <select> is scoped to its own card rather than looked up
    // form-wide, so a second plan card could never echo Subscribe's cadence.
    var syncFreqSelect = function () {
      form.querySelectorAll("[data-spark-freq-select]").forEach(function (sel) {
        var scope = sel.closest("[data-spark-pdp-card]") || form;
        var checked = scope.querySelector(
          "[data-spark-freq-radios] [data-spark-pdp-plan]:checked",
        );
        if (checked && checked.value && sel.value !== checked.value) {
          sel.value = checked.value;
          // Keep the custom face in step — setting .value fires no event.
          if (sel._sparkSelRefresh) sel._sparkSelRefresh();
        }
      });
    };

    // The nudge and its undo live in the guard above, which reads the form live.
    var promptSize = function () {
      showBlocked(guardParts(), "size");
    };
    var clearSizePrompt = clearBlocked;

    // One price when the numbers agree, "From" the lowest when they do not
    // (sizes on this catalogue are priced per pack, so they usually differ).
    var priceLabel = function (list) {
      var lo = Math.min.apply(null, list);
      var hi = Math.max.apply(null, list);
      return (lo === hi ? "" : "From ") + money(lo);
    };

    var update = function () {
      var qty = clampQty();
      var planInput = checkedInput(planInputs);
      var planId = planInput ? planInput.value : "";
      if (planId) lastPlanId = planId;

      planCards.forEach(function (card) {
        card.classList.toggle(
          "is-selected",
          !!card.querySelector("[data-spark-pdp-plan]:checked"),
        );
      });

      if (!data) return;

      var variantId = null;
      var variant = null;
      var unpicked = sizeUnpicked();
      // pool: every variant the shopper could still end up with. Just the one
      // once the size is chosen; while it is not, every buyable size of the
      // other selections (all sizes when none is in stock, so it still prices).
      var pool = [];
      if (optionInputs.length) {
        var matches = matchingVariants();
        if (matches.length) {
          variantId = matches[0].id;
          variant = matches[0].variant;
        }
        if (unpicked) {
          matches.forEach(function (m) {
            if (m.variant.available) pool.push(m.variant);
          });
          if (!pool.length) {
            matches.forEach(function (m) {
              pool.push(m.variant);
            });
          }
        }
      } else if (idInput) {
        variantId = idInput.value;
        variant = data.variants[variantId] || null;
      }

      // No variant carries the selected option combination.
      if (!variant) {
        setText("[data-spark-pdp-cta-label]", "Unavailable");
        setCtasDisabled(true);
        return;
      }

      if (!pool.length) pool = [variant];
      var poolAvailable = pool.some(function (v) {
        return v.available;
      });

      // Nothing is posted until a size is chosen: an empty id is refused by the
      // cart, the stand-in variant would be a size nobody picked.
      if (idInput) {
        if (unpicked) idInput.value = "";
        else if (variantId) idInput.value = variantId;
      }
      if (wallets) wallets.hidden = unpicked;

      // Re-filter the gallery so only shared + this variant's images remain.
      if (variantId) filterGallery(variantId);

      // Pieces-per-pack pill on each purchase-option card. The count is a
      // property of the selected size, so it follows the resolved variant;
      // hide the pill for any variant/product without a count.
      document
        .querySelectorAll("[data-spark-pdp-pieces]")
        .forEach(function (el) {
          if (variant.pieces && !unpicked) {
            el.textContent = variant.pieces + " per pack";
            el.hidden = false;
          } else {
            el.textContent = "";
            el.hidden = true;
          }
        });

      var plan = planId ? findPlan(variant, planId) : null;
      var unit = plan ? plan.price_cents : variant.price_cents;
      var label = variant.available
        ? (plan ? "Subscribe" : "Add to cart") + " · " + money(unit * qty)
        : "Sold out";
      if (unpicked) label = poolAvailable ? "Choose your size" : "Sold out";
      setText("[data-spark-pdp-cta-label]", label);
      setCtasDisabled(unpicked ? !poolAvailable : !variant.available);

      var stickyJump = document.querySelector("[data-spark-pdp-sticky-jump]");
      if (stickyJump) {
        stickyJump.setAttribute(
          "aria-label",
          unpicked
            ? "Choose your size"
            : "Choose your options" + (variant.available ? " and subscribe" : ""),
        );
      }

      // The gallery follows the variant when it has its own image — and leads
      // with it, rather than scrolling the strip to whatever position it holds.
      if (variant.image && variant.image.src) {
        setMainImage(
          variant.image.src,
          variant.image.srcset,
          variant.image.alt,
        );
        leadWithImage(variant.image.src);
      }

      // Each card prices itself off its OWN plans — the subscribe card off the
      // selected cadence's allocation, the one-time card off the plain variant
      // price. They must never share a value.
      //
      // Priced over the pool: with the size chosen that is the one variant and
      // every number is exactly what it was. Unchosen, each price is "From" the
      // lowest size, the strike-through only shows when every size shares one
      // full price, and the percentage is the smallest any size gets.
      planCards.forEach(function (card) {
        var centsList = [];
        var baseList = [];
        var percents = [];
        var saves = [];
        pool.forEach(function (v) {
          var vPlan = findPlan(v, cardPlanId(card, v));
          var vCents = vPlan ? vPlan.price_cents : v.price_cents;
          centsList.push(vCents);
          baseList.push(v.price_cents);
          percents.push(
            v.price_cents > 0
              ? Math.round((1 - vCents / v.price_cents) * 100)
              : 0,
          );
          saves.push(Math.max(0, v.price_cents - vCents));
        });

        var priceEl = card.querySelector("[data-spark-pdp-card-price]");
        if (priceEl) priceEl.textContent = priceLabel(centsList);

        var percent = Math.min.apply(null, percents);
        var oneBase =
          Math.min.apply(null, baseList) === Math.max.apply(null, baseList);

        // Struck-through full price only means something when there IS a saving.
        var compareEl = card.querySelector("[data-spark-pdp-card-compare]");
        if (compareEl) {
          compareEl.textContent = money(baseList[0]);
          compareEl.style.display = percent > 0 && oneBase ? "" : "none";
        }

        var saveEl = card.querySelector("[data-spark-pdp-card-save]");
        if (saveEl) {
          saveEl.style.display = percent > 0 ? "" : "none";
          if (percent > 0) {
            // The badge is a sentence with the percentage in the middle
            // ("Most popular · Save 10% off this and every delivery"), so it
            // carries both halves as data attributes rather than only a prefix.
            var prefix = saveEl.getAttribute("data-save-prefix") || "";
            var suffix = saveEl.getAttribute("data-save-suffix") || "";
            // CR pdpsv: dual-unit "Save 10% ($2.26) every delivery" (OTP − sub).
            // Unchosen size whose savings differ by size: "up to" the largest.
            if (saveEl.getAttribute("data-cr-save-mode") === "pct-dollars") {
              var saveLo = Math.min.apply(null, saves);
              var saveHi = Math.max.apply(null, saves);
              saveEl.textContent =
                prefix + "Save " + percent + "% (" +
                (saveLo === saveHi ? "" : "up to ") + money(saveHi) + ")" + suffix;
            } else {
              saveEl.textContent = prefix + "Save " + percent + "%" + suffix;
            }
          }
        }
      });

      // If the size change invalidated the checked plan, fall back to the
      // first still-buyable option so the form can never post a dead plan.
      if (planId && !findPlan(variant, planId)) {
        var recovered = false;
        for (var c = 0; c < planCards.length; c += 1) {
          var candidate = cardPlanId(planCards[c], variant);
          if (!candidate) continue;
          var radios = planCards[c].querySelectorAll("[data-spark-pdp-plan]");
          for (var r = 0; r < radios.length; r += 1) {
            if (radios[r].value === candidate) {
              radios[r].checked = true;
              recovered = true;
              break;
            }
          }
          if (recovered) break;
        }
        if (!recovered && oneCard) {
          var oneInput = oneCard.querySelector("[data-spark-pdp-plan]");
          if (oneInput) {
            oneInput.checked = true;
            recovered = true;
          }
        }
        // Only re-enter when a radio actually moved. Recursing unconditionally
        // would spin forever on a product with no recoverable option.
        if (recovered) {
          update();
          return;
        }
      }

      // Products with no selling plans at all render a bare price paragraph
      // instead of cards, so it still needs updating on variant change.
      setText(
        "[data-spark-pdp-onetime-price]",
        priceLabel(
          pool.map(function (v) {
            return v.price_cents;
          }),
        ),
      );

      var meta = [];
      if (!unpicked && variant.title && variant.title !== "Default Title") {
        meta.push(variant.title);
      }
      // Name the group the CHOSEN plan belongs to, falling back to the group the
      // sticky bar was rendered with.
      var stickyEl = document.querySelector("[data-spark-pdp-sticky]");
      var groupName =
        (planInput && planInput.getAttribute("data-group-name")) ||
        (stickyEl && stickyEl.getAttribute("data-plan-group-name"));
      meta.push(plan ? groupName || plan.name : "One-time purchase");
      setText("[data-spark-pdp-sticky-meta]", meta.join(" · "));

      // Mirror the checked cadence radio into the visible frequency <select>
      // (the select is the UI, the radios are the form). Runs last so it
      // reflects any recovery fallback above.
      syncFreqSelect();
    };

    optionInputs.forEach(function (input) {
      input.addEventListener("change", update);
    });
    if (sizeSelect) sizeSelect.addEventListener("change", clearSizePrompt);

    // The guard re-reads the controls through this before it refuses an add.
    guardResync = function () {
      update();
    };
    planInputs.forEach(function (input) {
      input.addEventListener("change", update);
    });
    qtyPresets.forEach(function (radio) {
      radio.addEventListener("change", update);
    });
    // − and +: select the tile and move its number. The buttons sit inside the tile's
    // <label>, and a click on a button inside a label does not activate the label's
    // radio, so the selection is made here. preventDefault keeps the label out of it.
    var stepMore = function (delta) {
      return function (event) {
        event.preventDefault();
        if (!qtyMoreRadio) return;
        setMore(parseInt(qtyMoreRadio.value, 10) + delta, true);
        update();
      };
    };
    if (qtyMoreMinus) qtyMoreMinus.addEventListener("click", stepMore(-1));
    if (qtyMorePlus) qtyMorePlus.addEventListener("click", stepMore(1));
    // A restored page can bring the radio back on a number the face does not show.
    if (qtyMoreRadio) setMore(parseInt(qtyMoreRadio.value, 10), false);

    // Frequency <select> -> hidden cadence radio. Checking the radio keeps the
    // shared name="selling_plan" radio-group semantics (picking a cadence
    // unchecks the one-time card) and every existing code path intact.
    form.querySelectorAll("[data-spark-freq-select]").forEach(function (sel) {
      sel.addEventListener("change", function () {
        var scope = sel.closest("[data-spark-pdp-card]") || form;
        var radios = scope.querySelectorAll(
          "[data-spark-freq-radios] [data-spark-pdp-plan]",
        );
        for (var i = 0; i < radios.length; i += 1) {
          if (radios[i].value && radios[i].value === sel.value) {
            if (!radios[i].checked) {
              radios[i].checked = true;
              update();
            }
            return;
          }
        }
      });
    });

    // Clicking a plan card anywhere outside its own controls selects that
    // card — its last chosen frequency, else its first. Scoped per card, so a
    // click can only ever select one of that card's own cadences.
    planCards.forEach(function (card) {
      if (card.getAttribute("data-spark-pdp-card") === "one") return; // native <label>
      card.addEventListener("click", function (event) {
        if (event.target.closest("label, input, button, a, select")) return;
        var fallback = null;
        var match = null;
        card.querySelectorAll("[data-spark-pdp-plan]").forEach(function (input) {
          if (!input.value || input.disabled) return;
          if (!fallback) fallback = input;
          if (input.value === lastPlanId) match = input;
        });
        var target = match || fallback;
        if (target && !target.checked) {
          target.checked = true;
          update();
        }
      });
    });

    /* --------------------------------------------------------------
       Sticky bar: slides up once the buybox CTA scrolls out above the
       viewport, and takes the shopper back to the options when tapped.
       -------------------------------------------------------------- */
    var sticky = document.querySelector("[data-spark-pdp-sticky]");
    var cta = form.querySelector("[data-spark-pdp-cta]");
    if (sticky && cta) {
      /* MEASURED, NOT REMEMBERED. This was an IntersectionObserver alone, acting
         on entry.boundingClientRect from the callback — and the observer only
         speaks when the CTA crosses the viewport edge. Everything that moves the
         page WITHOUT such a crossing left the bar showing the last thing it was
         told: the reviews widget injecting six cards above the fold line, the
         trust band's iframe revealing its height, the gallery settling, a phone
         rotating, the URL bar collapsing on scroll. Hence "sometimes it's
         hidden" — the state was stale, not wrong at the time.

         Reading the rect at the moment we decide cannot go stale. The observer
         stays as the cheap primary trigger; scroll and resize are the backstop,
         both funnelled through one rAF so a fast scroll costs one measurement
         per frame. */
      var stickyQueued = false;

      /* TEST "pdp-atf-v1" (ABConvert): on a phone the bar is there from the first
         screen — shown whenever the main button is off screen, BELOW as well as
         above. ON only while <html> or <body> carries the class `pdp-atf-v1` (the
         variant's custom JS adds it) or the URL has ?pdp_atf=1, which adds it (for
         looking at it yourself) — same switch as assets/clear-lp-tap.js. Without
         it every branch below falls through to the control's single rule, and the
         root's padding is never touched.
         ASKED ON EVERY SYNC: ABConvert may add the class after this deferred script
         runs, so a MutationObserver on <html>/<body> re-syncs when it lands (no
         scroll needed). The body observer also catches the fit-kit sheet opening
         (clr-sheet-open), during which the variant bar stands down. */
      var ATF_CLS = "pdp-atf-v1";
      var docEl = document.documentElement;
      try {
        if (new URLSearchParams(window.location.search).get("pdp_atf") === "1") {
          docEl.classList.add(ATF_CLS);
        }
      } catch (e) {}
      var atfPhone = window.matchMedia("(max-width: 920px)");
      var pdpRoot = sticky.closest("[data-spark-pdp-root]");
      var sizeModal = document.querySelector("[data-spark-size-modal]");
      var atfPadded = false;
      var atfLast = false; // the flag's answer at the last sync
      var atfOn = function () {
        return (
          docEl.classList.contains(ATF_CLS) ||
          !!(document.body && document.body.classList.contains(ATF_CLS))
        );
      };
      // The fit-kit finder / chart (native <dialog>, flags body) or the plain size modal.
      var sizeSheetOpen = function () {
        return (
          !!(document.body && document.body.classList.contains("clr-sheet-open")) ||
          !!(sizeModal && sizeModal.classList.contains("flex"))
        );
      };

      function syncSticky() {
        stickyQueued = false;
        var r = cta.getBoundingClientRect();
        var atf = atfOn() && atfPhone.matches;
        atfLast = atf;
        /* Control: fully above the viewport — the buy controls are behind the
           shopper. Variant: also while they are still below the first screen. */
        var show = r.bottom < 0 || (atf && r.top > window.innerHeight);
        if (atf && sizeSheetOpen()) show = false;
        sticky.classList.toggle("is-visible", show);
        /* Variant only: room at the foot of the PDP for the bar (its own height
           already includes env(safe-area-inset-bottom)), so the last trust row is
           not under it. Undone if the flag or the phone width goes away. */
        if (pdpRoot && (atf || atfPadded)) {
          pdpRoot.style.paddingBottom = atf ? sticky.offsetHeight + "px" : "";
          atfPadded = atf;
        }
      }

      function queueSticky() {
        if (stickyQueued) return;
        stickyQueued = true;
        window.requestAnimationFrame(syncSticky);
      }

      if ("IntersectionObserver" in window) {
        new IntersectionObserver(queueSticky, { threshold: 0 }).observe(cta);
      }
      window.addEventListener("scroll", queueSticky, { passive: true });
      window.addEventListener("resize", queueSticky, { passive: true });
      /* Extra triggers for the variant only — the control keeps exactly its
         observer + scroll + resize. atfLast lets a removed class undo itself. */
      var queueAtf = function () {
        if (atfOn() || atfLast) queueSticky();
      };
      if ("MutationObserver" in window) {
        var atfWatch = new MutationObserver(queueAtf);
        atfWatch.observe(docEl, { attributes: true, attributeFilter: ["class"] });
        if (document.body) {
          atfWatch.observe(document.body, { attributes: true, attributeFilter: ["class"] });
        }
        if (sizeModal) {
          atfWatch.observe(sizeModal, { attributes: true, attributeFilter: ["class"] });
        }
      }
      window.addEventListener("load", queueAtf);
      syncSticky();

      /* Tap = go choose. See the header of snippets/spark-pdp-sticky.liquid for
         why this scrolls instead of submitting the buy form.

         IT SCROLLS TO THE CHOICE, AND OPENS NOTHING. The first pass landed on the
         buy box and focused the option control itself, which puts a picker on
         screen before the shopper has looked at anything: a native <select>
         summons the OS wheel on a phone, and the themed face takes the focus ring
         either way. Arriving to an open dropdown is not arriving.

         So the target is the first option FIELD — the size or colour group,
         whichever the product leads with — and focus goes to the field's
         container, not to the control inside it. That still moves a screen
         reader's reading position to the group (the fieldset carries a legend),
         which a bare scroll would not, without touching anything that opens.
         preventScroll because the browser's own scroll-into-view for focus would
         fight the smooth one just started. */
      var jump = sticky.querySelector("[data-spark-pdp-sticky-jump]");
      if (jump) {
        jump.addEventListener("click", function () {
          // Size still unchosen: the same nudge as the main button.
          if (sizeUnpicked()) {
            promptSize();
            return;
          }
          var field =
            form.querySelector(".spark-pdp-field") ||
            document.querySelector(".spark-pdp-buybox") ||
            form;
          field.scrollIntoView({ behavior: "smooth", block: "start" });
          if (field.hasAttribute("tabindex")) {
            try {
              field.focus({ preventScroll: true });
            } catch (e) {
              /* Older browsers ignore the options bag; a jump is better than
                 nothing, and the smooth scroll has already been queued. */
            }
          }
        });
      }
    }

    var initialPlan = checkedInput(planInputs);
    if (initialPlan && initialPlan.value) lastPlanId = initialPlan.value;

    // Filter the gallery to the initially-selected variant before the first
    // paint of update() (which re-filters on every option/plan change).
    var initVariantId = idInput && idInput.value;
    if (initVariantId) filterGallery(initVariantId);

    update();

    // RESTORED FORM STATE. On a reload or a back/forward visit the browser can put
    // the shopper's last size back into the <select> after the update() above has
    // run, without firing a change event. Read the controls again once the page
    // has fully loaded and whenever it is shown from the back/forward cache, so the
    // id, the prices and the button agree with what is on screen.
    window.addEventListener("load", update);
    window.addEventListener("pageshow", function (event) {
      if (event.persisted) update();
    });
  }

  // CR harvest pdpeta — dated ETA merged into first .spark-pdp-buybox__terms span.
  // +5 US business days (skip Sat/Sun), same spirit as account-v2 estimatedArrivalDate.
  (function () {
    var WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    var MONTHS = [
      "Jan", "Feb", "Mar", "Apr", "May", "Jun",
      "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
    ];
    function addBusinessDays(from, n) {
      var d = new Date(from.getFullYear(), from.getMonth(), from.getDate());
      var added = 0;
      while (added < n) {
        d.setDate(d.getDate() + 1);
        var day = d.getDay();
        if (day !== 0 && day !== 6) added += 1;
      }
      return d;
    }
    function formatArrivesBy(d) {
      return WEEKDAYS[d.getDay()] + ", " + MONTHS[d.getMonth()] + " " + d.getDate();
    }
    var termsList = document.querySelectorAll(
      '.spark-pdp-buybox__terms[data-cr-eta="v2"]',
    );
    for (var i = 0; i < termsList.length; i += 1) {
      var terms = termsList[i];
      var first = terms.querySelector("span");
      if (!first) continue;
      var etaDate = addBusinessDays(new Date(), 5);
      var dateStr = formatArrivesBy(etaDate);
      first.textContent =
        "\u2713 Usually arrives by " + dateStr + " \u00b7 Ships discreetly";
      first.setAttribute("data-cr-arrives-by", dateStr);
    }
  })();

  // "What's my size?" modal: open from any [data-spark-size-open], close on the
  // ✕, the backdrop, or Escape. Self-contained; no-op if the modal isn't present.
  (function () {
    var modal = document.querySelector("[data-spark-size-modal]");
    if (!modal) return;
    var open = function () {
      modal.classList.remove("hidden");
      modal.classList.add("flex");
      document.body.style.overflow = "hidden";
    };
    var close = function () {
      modal.classList.add("hidden");
      modal.classList.remove("flex");
      document.body.style.overflow = "";
    };
    document.querySelectorAll("[data-spark-size-open]").forEach(function (btn) {
      btn.addEventListener("click", open);
    });
    modal.querySelectorAll("[data-spark-size-close]").forEach(function (btn) {
      btn.addEventListener("click", close);
    });
    modal.addEventListener("click", function (e) {
      if (e.target === modal) close();
    });
    document.addEventListener("keydown", function (e) {
      if (e.key === "Escape" && modal.classList.contains("flex")) close();
    });
  })();

})();
